import { recordOperationOutcome, isUnknownAcknowledgement } from '../utils/operationDiagnostics';
import { createOrderMutation } from '@msales/shared/graphql/mutations/order/createOrder';
import { createCustomerReturnMutation } from '@msales/shared/graphql/mutations/customerReturn/createCustomerReturn';
import { insertLocationMutation } from '@msales/shared/graphql/mutations/location/insertLocationEvent';
import { answerSurveyMutation } from '@msales/shared/graphql/mutations/survey/answerSurvey';
import { createTasksCommentMutation } from '@msales/shared/graphql/mutations/taskN/createTasksComment';
import { updateTaskStatusMutation } from '@msales/shared/graphql/mutations/taskN/updateTaskStatus';
import { createTaskExecutionMutation } from '@msales/shared/graphql/mutations/taskN/createTaskExecution';
import { createNewCustomerRequestMutation } from '@msales/shared/graphql/mutations/newCustomer/createNewCustomerRequest';
import gql from 'graphql-tag';
import increaseOrderCount from '../utils/increaseIndexDbOrderCount';
import axios from 'axios';
import { markOrderAsSent, markOrderAsFailed } from '../utils/localOrderBackup';
import {
  markReceiptSynced,
  markReceiptFailed,
  getReceiptByMobileKey,
} from '../utils/receiptService';
import localforage from 'localforage';
import {
  LOCAL_TASK_STATUSES,
  markTaskModificationsAsSentByOperationId,
  TASK_MOD_SYNC_STATES,
} from '../hooks/useTaskModifications';
import { getSafeToken } from '../utils/firebaseTokenHelper';
import { sanitizeSurveyDistributionAnswers } from '../utils/surveyDistributionUtils';
import { isRetriableSyncError } from '../utils/offlineSyncHelpers';
import { getTaskUnavailableMessage } from '../utils/taskSyncErrors';
import { requiresPricingReview } from '../utils/orderPricingReview';
import { associatePhotoOrder, syncFieldPhotoUpload, syncFieldPhotoContext } from '../utils/fieldPhotos';
import { processSyncOperations } from '../utils/photoSyncQueue';

const CREATE_VISIT_MUTATION = gql`
  mutation CreateVisit($input: CreateVisitInput!) {
    createVisit(input: $input)
  }
`;

/**
 * Centralized offline sync service
 * Handles all operation types independently of component lifecycle
 * Works with Apollo Client directly (no hooks required)
 */
class OfflineSyncService {
  constructor(apolloClient) {
    this.client = apolloClient;
    this.inFlightFieldPhotos = new Map();
  }

  gcCache() {
    try {
      const cache = this.client && this.client.cache;
      if (cache && typeof cache.gc === 'function') {
        cache.gc();
      }
    } catch (e) {
      console.warn('[OfflineSyncService] cache.gc() failed:', e && e.message);
    }
  }

  /**
   * Execute a mutation using Apollo Client directly
   * Includes a timeout to prevent hanging on unstable networks
   */
  async executeMutation(mutation, variables, timeoutMs = 30000) {
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () =>
          reject(
            new Error('Sync mutation timeout - server did not respond in time'),
          ),
        timeoutMs,
      );
    });

    try {
      return await Promise.race([
        this.client.mutate({ mutation, variables }),
        timeoutPromise,
      ]);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async runPostOrderStep(label, fn, operationId) {
    try {
      return await fn();
    } catch (error) {
      if (operationId) recordOperationOutcome({ id: operationId, type: 'INSERT_ORDER' }, 'accepted_reconciliation_failed', { source: 'service', reason: 'reconciliation' });
      console.warn(`[SyncService] post-order step "${label}" failed:`, error);
      return null;
    }
  }

  async handleInsertOrder(variables, operationId) {
    const operation = { id: operationId, type: 'INSERT_ORDER' };
    const post = (label, fn) => this.runPostOrderStep(label, fn, operationId);
    recordOperationOutcome(operation, 'sending', { source: 'service' });
    try {
      const { fieldPhotoSessionId, ...orderVariables } = variables;
      let referenceNumber = variables.referenceNumber;
      if (!referenceNumber) {
        const existingReceipt = await getReceiptByMobileKey(operationId);
        referenceNumber = existingReceipt?.referenceNumber;
      }

      const result = await this.executeMutation(createOrderMutation, {
        ...orderVariables,
        mobileKey: operationId,
        referenceNumber: referenceNumber || null,
      });

      if (!result || !result.data) {
        throw new Error('createOrder mutation returned no data');
      }

      const orderId = result.data?.createOrder?.id;
      const orderMessage = result.data?.createOrder?.message;
      const customerID = variables.input?.customerID;

      if (orderId) {
        recordOperationOutcome(operation, 'server_accepted', { source: 'service' });
        await post('associatePhotoOrder', () =>
          associatePhotoOrder(fieldPhotoSessionId, orderId, referenceNumber),
        );
        await post('markReceiptSynced', () =>
          referenceNumber ? markReceiptSynced(referenceNumber, orderId) : null,
        );

        const lat = variables.lat || variables.input?.lat;
        const lng = variables.lng || variables.input?.lng;
        if (lat || lng) {
          await post('insertLocation', () =>
            this.insertLocation(orderId, variables),
          );
        }

        if (customerID) {
          await post('increaseOrderCount', () =>
            increaseOrderCount(customerID),
          );
        }

        await post('markOrderAsSent', () =>
          markOrderAsSent(operationId, orderId),
        );
        await post('markTaskModificationsAsSent', () =>
          markTaskModificationsAsSentByOperationId(
            operationId,
            (mod) =>
              mod.rasterCompletedByOrder || mod.ulistavanjeCompletedByOrder,
          ),
        );
      } else if (
        orderMessage &&
        (orderMessage.includes('already exists') ||
          orderMessage.includes('Key already exists'))
      ) {
        recordOperationOutcome(operation, 'server_accepted', { source: 'service' });
        await post('markOrderAsSent(duplicate)', () =>
          markOrderAsSent(operationId, 'duplicate'),
        );
        await post('markTaskModificationsAsSent(duplicate)', () =>
          markTaskModificationsAsSentByOperationId(
            operationId,
            (mod) =>
              mod.rasterCompletedByOrder || mod.ulistavanjeCompletedByOrder,
          ),
        );
      } else {
        recordOperationOutcome(operation, 'acknowledgement_unknown', { source: 'service', reason: 'no_ack' });
        await this.runPostOrderStep('markOrderAsSent(noId)', () =>
          markOrderAsSent(operationId, null),
        );
      }

      return result;
    } catch (error) {
      recordOperationOutcome(operation, isUnknownAcknowledgement(error) ? 'acknowledgement_unknown' : 'retrying', { source: 'service', reason: isUnknownAcknowledgement(error) ? 'network' : 'none' });
      console.error('[SyncService] INSERT_ORDER failed:', error);

      await this.runPostOrderStep('markOrderAsFailed', () =>
        markOrderAsFailed(operationId, error),
      );

      await this.runPostOrderStep('markReceiptFailed', async () => {
        const existingReceipt = await getReceiptByMobileKey(operationId);
        if (existingReceipt?.referenceNumber) {
          await markReceiptFailed(
            existingReceipt.referenceNumber,
            error.message,
          );
        }
      });

      throw error;
    }
  }

  /**
   * CREATE_CUSTOMER_RETURN handler
   * Handles return order creation
   */
  async handleCreateCustomerReturn(variables, operationId) {
    try {
      // Execute the mutation with mobileKey
      const result = await this.executeMutation(createCustomerReturnMutation, {
        ...variables,
        mobileKey: operationId,
      });

      const returnId = result.data?.createCustomerReturn?.id;
      const returnMessage = result.data?.createCustomerReturn?.message;

      if (returnId) {
        await markOrderAsSent(operationId, returnId);
      } else if (returnMessage && returnMessage.includes('already exists')) {
        console.log(
          '[SyncService] Duplicate return detected, marking local backup as sent:',
          returnMessage,
        );
        await markOrderAsSent(operationId, 'duplicate');
      } else {
        await markOrderAsSent(operationId, null);
      }

      return result;
    } catch (error) {
      console.error('[SyncService] CREATE_CUSTOMER_RETURN failed:', error);
      await markOrderAsFailed(operationId, error);
      throw error;
    }
  }

  /**
   * INSERT_LOCATION_EVENT handler
   * Handles standalone location event insertion
   */
  async handleInsertLocationEvent(variables, operationId) {
    try {
      return await this.executeMutation(insertLocationMutation, {
        ...variables,
        mobileKey: operationId,
      });
    } catch (error) {
      console.error('[SyncService] INSERT_LOCATION_EVENT failed:', error);
      throw error;
    }
  }

  /**
   * CREATE_VISIT handler
   * Handles customer visit creation
   */
  async handleCreateVisit(variables, operationId) {
    try {
      const result = await this.executeMutation(
        CREATE_VISIT_MUTATION,
        variables,
      );
      console.log('[SyncService] Visit synced:', operationId, 'customer:', variables?.input?.customer);
      return result;
    } catch (error) {
      console.error('[SyncService] CREATE_VISIT failed:', operationId, error);
      throw error;
    }
  }

  /**
   * ANSWER_SURVEY handler
   * Handles survey answer submission
   */
  async handleAnswerSurvey(variables, operationId) {
    try {
      const { batEmployee, ...mutationVariables } = variables || {};
      let sanitizedVariables = mutationVariables;
      if (!batEmployee) {
        try {
          const customersStore = localforage.createInstance({ name: 'Customers' });
          const [surveys, batProducts] = await Promise.all([
            customersStore.getItem('surveysNodes'),
            customersStore.getItem('batSubjektProducts'),
          ]);
          const survey = (surveys || []).find(
            ({ id }) => String(id) === String(mutationVariables?.input?.id),
          );
          sanitizedVariables = sanitizeSurveyDistributionAnswers({
            variables: mutationVariables,
            survey,
            batProducts,
          });
        } catch (error) {
          console.warn(
            '[SyncService] Failed to validate survey distribution products:',
            error,
          );
        }
      }

      const result = await this.executeMutation(
        answerSurveyMutation,
        sanitizedVariables,
      );
      if (result?.data?.answerSurvey === false || result?.data?.answerSurvey === null) {
        throw new Error('Server did not confirm survey answer (returned false/null)');
      }
      return result;
    } catch (error) {
      console.error('[SyncService] ANSWER_SURVEY failed:', error);
      throw error;
    }
  }

  /**
   * CREATE_COMMENT handler
   * Handles task comment creation
   */
  async handleCreateComment(variables, operationId) {
    try {
      return await this.executeMutation(createTasksCommentMutation, variables);
    } catch (error) {
      console.error('[SyncService] CREATE_COMMENT failed:', error);
      throw error;
    }
  }

  /**
   * UPDATE_TASK_STATUS handler
   * Handles task status changes (start, finish, cancel)
   */
  async handleUpdateTaskStatus(variables, operationId) {
    try {
      const result = await this.executeMutation(
        updateTaskStatusMutation,
        variables,
      );
      await this.updateTaskModificationAfterSync(
        variables.taskID,
        variables.newStatusCode,
      );
      console.log('[SyncService] Task status synced:', variables.taskID);
      return result;
    } catch (error) {
      console.error('[SyncService] UPDATE_TASK_STATUS failed:', error);
      throw error;
    }
  }

  /**
   * CREATE_TASK_EXECUTION handler
   * Handles task execution with comment/photo/number (for modal tasks)
   * Also handles the status update that follows execution
   */
  async handleCreateTaskExecution(variables, operationId) {
    try {
      let uploadedFilename = null;
      if (variables.photoData) {
        uploadedFilename = await this.uploadTaskExecutionPhoto(
          variables,
          operationId,
        );
      }

      const executionInput = {
        ...variables.input,
        slikaUrl: uploadedFilename || variables.input.slikaUrl,
      };

      const result = await this.executeMutation(createTaskExecutionMutation, {
        input: executionInput,
      });

      if (result?.data?.createTaskExecution === false || result?.data?.createTaskExecution === null) {
        throw new Error('Server did not confirm task execution (returned false/null)');
      }

      const taskId = variables.taskID || variables.input?.taskSubjectId;

      if (variables.statusUpdate) {
        await this.executeMutation(updateTaskStatusMutation, {
          taskID: variables.statusUpdate.taskID,
          oldStatusCode: variables.statusUpdate.oldStatusCode,
          newStatusCode: variables.statusUpdate.newStatusCode,
          lat: variables.lat || null,
          lon: variables.lng || null,
        });
        if (taskId) {
          await this.updateTaskModificationAfterSync(
            taskId,
            variables.statusUpdate.newStatusCode,
          );
        }
      } else if (taskId) {
        await this.clearTaskModification(taskId);
      }

      return result;
    } catch (error) {
      console.error('[SyncService] CREATE_TASK_EXECUTION failed:', error);
      throw error;
    }
  }

  /**
   * Helper: Upload task execution photo
   */
  async uploadTaskExecutionPhoto(variables, operationId) {
    try {
      if (!variables.photoData) return null;

      const response = await fetch(variables.photoData);
      const blob = await response.blob();
      const file = new File([blob], variables.photoFileName || 'photo.jpg', {
        type: variables.photoFileType || 'image/jpeg',
      });

      const token = await this.getAuthToken();

      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'taskID',
        variables.taskID || variables.input?.taskSubjectId,
      );

      if (variables.lat) formData.append('lat', variables.lat);
      if (variables.lng) formData.append('lng', variables.lng);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_DOCUMENTS_ENDPOINT}/upload/task-doc`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        },
      );

      if (uploadResponse.status !== 200) {
        throw new Error(uploadResponse.data?.message || 'Photo upload failed');
      }

      return uploadResponse.data?.filename;
    } catch (error) {
      console.error('[SyncService] Task photo upload failed:', error);
      throw error;
    }
  }

  /**
   * Helper: Mark task modification as synced (keep status, remove pending flag)
   * Status is kept until user does a real data fetch which will overwrite with server data
   */
  async clearTaskModification(taskId) {
    try {
      const taskModificationsStore = localforage.createInstance({
        name: 'TaskModifications',
      });
      const modifications =
        (await taskModificationsStore.getItem('modifications')) || {};
      const taskIdStr = String(taskId);

      if (modifications[taskIdStr]) {
        modifications[taskIdStr] = {
          ...modifications[taskIdStr],
          syncState: TASK_MOD_SYNC_STATES.SENT,
          synced: true,
          sentAt: new Date().toISOString(),
          syncedAt: new Date().toISOString(),
          pendingOperationId: null,
        };
        await taskModificationsStore.setItem('modifications', modifications);
      }
    } catch (error) {
      console.warn('[SyncService] Failed to update task modification:', error);
    }
  }

  /**
   * Helper: Update task modification to reflect the actual synced status
   * This ensures local display matches server state after partial sync
   */
  async updateTaskModificationAfterSync(taskId, syncedStatusCode) {
    try {
      const taskModificationsStore = localforage.createInstance({
        name: 'TaskModifications',
      });
      const modifications =
        (await taskModificationsStore.getItem('modifications')) || {};
      const taskIdStr = String(taskId);

      if (modifications[taskIdStr]) {
        const syncedStatus = LOCAL_TASK_STATUSES[syncedStatusCode] || {
          code: syncedStatusCode,
          final:
            syncedStatusCode.includes('FINAL') ||
              syncedStatusCode === 'CANCELED' ||
              syncedStatusCode === 'REJECTED'
              ? 'D'
              : 'N',
          description: syncedStatusCode,
        };

        modifications[taskIdStr] = {
          ...modifications[taskIdStr],
          status: syncedStatus,
          syncState: TASK_MOD_SYNC_STATES.SENT,
          synced: true,
          sentAt: new Date().toISOString(),
          syncedAt: new Date().toISOString(),
          pendingOperationId: null,
        };

        await taskModificationsStore.setItem('modifications', modifications);
      }
    } catch (error) {
      console.warn(
        '[SyncService] Failed to update task modification after sync:',
        error,
      );
    }
  }

  /**
   * CREATE_DOCUMENT handler
   * Handles task document upload (file restoration from base64)
   */
  async handleCreateDocument(variables, operationId) {
    try {
      if (!variables.fileData) {
        throw new Error('No file data available in queued operation');
      }

      // Reconstruct file from base64 data
      const response = await fetch(variables.fileData);
      const blob = await response.blob();
      const file = new File([blob], variables.fileName, {
        type: variables.fileType,
      });

      // Get token from localStorage/Firebase
      const token = await this.getAuthToken();

      // Prepare form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('taskID', variables.taskID);

      if (variables.lat) formData.append('lat', variables.lat);
      if (variables.lng) formData.append('lng', variables.lng);

      // Upload document
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_DOCUMENTS_ENDPOINT}/upload/task-doc`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        },
      );

      if (uploadResponse.status !== 200) {
        throw new Error(
          uploadResponse.data?.message || 'Document upload failed',
        );
      }

      return uploadResponse;
    } catch (error) {
      console.error('[SyncService] CREATE_DOCUMENT failed:', error);
      throw error;
    }
  }

  /**
   * UPLOAD_SURVEY_PHOTO handler
   * Handles survey photo upload (file restoration from base64)
   */
  async handleUploadSurveyPhoto(variables, operationId) {
    try {
      if (!variables.fileData) {
        throw new Error('No file data available in queued operation');
      }

      const response = await fetch(variables.fileData);
      const blob = await response.blob();
      const file = new File([blob], variables.fileName, {
        type: variables.fileType,
      });

      const token = await this.getAuthToken();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('surveyId', variables.surveyId);
      formData.append('questionId', variables.questionId);

      if (variables.pendingToken) {
        formData.append('pendingToken', variables.pendingToken);
      }
      if (variables.distributionPhoto) {
        formData.append('distributionPhoto', variables.distributionPhoto);
      }
      if (variables.artikalId) {
        formData.append('artikalId', variables.artikalId);
      }
      if (variables.lat) formData.append('lat', variables.lat);
      if (variables.lng) formData.append('lng', variables.lng);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_DOCUMENTS_ENDPOINT}/upload/survey-doc`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        },
      );

      if (uploadResponse.status !== 200) {
        throw new Error(
          uploadResponse.data?.message || 'Survey photo upload failed',
        );
      }

      if (variables.distributionPhoto === 'D' && variables.pendingToken) {
        await this.resolveDistributionPhotoToken(
          variables.pendingToken,
          uploadResponse.data?.filename,
        );
      }

      return uploadResponse;
    } catch (error) {
      console.error('[SyncService] UPLOAD_SURVEY_PHOTO failed:', error);
      throw error;
    }
  }

  /**
   * Replaces a pending distribution-photo token in the cached
   * surveyDistributionResults with the uploaded filename, so a later
   * re-submit carries the real filename forward instead of a stale token.
   */
  async resolveDistributionPhotoToken(pendingToken, filename) {
    if (!filename) return;
    try {
      const customersStore = localforage.createInstance({ name: 'Customers' });
      const results = await customersStore.getItem('surveyDistributionResults');
      if (!Array.isArray(results)) return;

      const endpoint = String(
        process.env.REACT_APP_DOCUMENTS_ENDPOINT || '',
      ).replace(/\/$/, '');
      let changed = false;

      const updated = results.map((row) => {
        if (!Array.isArray(row?.fotografije)) return row;
        const hasToken = row.fotografije.some(
          (photo) => photo?.pendingToken === pendingToken,
        );
        if (!hasToken) return row;
        changed = true;
        return {
          ...row,
          fotografije: row.fotografije.map((photo) =>
            photo?.pendingToken === pendingToken
              ? {
                filename,
                previewUrl: `${endpoint}/surveys/${encodeURIComponent(filename)}`,
              }
              : photo,
          ),
        };
      });

      if (changed) {
        await customersStore.setItem('surveyDistributionResults', updated);
      }
    } catch (error) {
      console.warn(
        '[SyncService] Failed to resolve distribution photo token:',
        error,
      );
    }
  }

  /**
   * UPLOAD_IMAGES handler
   * Handles KO image uploads (FormData)
   */
  async handleUploadImages(operation, operationId) {
    try {
      return await axios({
        method: 'POST',
        url: operation.url,
        data: operation.data,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('[SyncService] UPLOAD_IMAGES failed:', error);
      throw error;
    }
  }

  /**
   * Helper: Get Firebase auth token with timeout and retry to prevent hanging
   */
  async getAuthToken() {
    const firebase = await import('firebase/app');
    const currentUser = firebase.default.auth().currentUser;

    // Shared helper handles cached-token retries, a forced refresh for expired
    // tokens, offline short-circuit and Sentry reporting. Throw on empty so the
    // failed operation stays queued and retries with a fresh token instead of
    // uploading with an empty Bearer (which the file server rejects with 401).
    const token = await getSafeToken(currentUser, 5000, false);
    if (!token) {
      throw new Error('Sesija je istekla. Osvježite prijavu i pokušajte ponovo.');
    }
    return token;
  }

  /**
   * Helper: Insert location event
   * Used as post-processing after order creation
   */
  async insertLocation(docId, variables) {
    try {
      await this.executeMutation(insertLocationMutation, {
        location: {
          lat: variables.lat || variables.input?.lat || null,
          lng: variables.lng || variables.input?.lng || null,
          alt: variables.alt || variables.input?.alt || null,
          customerLat: variables.input?.customerLat || null,
          customerLng: variables.input?.customerLng || null,
          startTime: variables.input?.startTime || null,
          eventType: 1,
          docId: parseInt(docId) || -1,
        },
      });
    } catch (error) {
      console.warn('[SyncService] Location insert failed:', error);
    }
  }

  async handleCreateNewCustomer(variables, operationId) {
    try {
      return await this.executeMutation(createNewCustomerRequestMutation, variables);
    } catch (error) {
      console.error('[SyncService] CREATE_NEW_CUSTOMER failed:', error);
      throw error;
    }
  }

  /**
   * Main dispatcher - routes operation type to handler
   */
  executeOperation(operation) {
    const isFieldPhoto = operation.type === 'UPLOAD_FIELD_PHOTO' || operation.type === 'UPDATE_FIELD_PHOTO_CONTEXT';
    if (!isFieldPhoto) return this.executeOperationOnce(operation);
    if (this.inFlightFieldPhotos.has(operation.id)) return this.inFlightFieldPhotos.get(operation.id);
    const pending = this.executeOperationOnce(operation).finally(() => this.inFlightFieldPhotos.delete(operation.id));
    this.inFlightFieldPhotos.set(operation.id, pending);
    return pending;
  }

  async executeOperationOnce(operation) {
    const { type, variables, id } = operation;
    if (requiresPricingReview(operation)) {
      return { pendingPricingReview: true };
    }

    switch (type) {
      case 'UPLOAD_FIELD_PHOTO':
        return await syncFieldPhotoUpload(variables);

      case 'UPDATE_FIELD_PHOTO_CONTEXT':
        return await syncFieldPhotoContext(variables);

      case 'INSERT_ORDER':
        return await this.handleInsertOrder(variables, id);

      case 'CREATE_CUSTOMER_RETURN':
        return await this.handleCreateCustomerReturn(variables, id);

      case 'INSERT_LOCATION_EVENT':
        return await this.handleInsertLocationEvent(variables, id);

      case 'CREATE_VISIT':
        return await this.handleCreateVisit(variables, id);

      case 'ANSWER_SURVEY':
        return await this.handleAnswerSurvey(variables, id);

      case 'CREATE_COMMENT':
        return await this.handleCreateComment(variables, id);

      case 'UPDATE_TASK_STATUS':
        return await this.handleUpdateTaskStatus(variables, id);

      case 'CREATE_TASK_EXECUTION':
        return await this.handleCreateTaskExecution(variables, id);

      case 'CREATE_DOCUMENT':
        return await this.handleCreateDocument(variables, id);

      case 'UPLOAD_SURVEY_PHOTO':
        return await this.handleUploadSurveyPhoto(variables, id);

      case 'UPLOAD_IMAGES':
        return await this.handleUploadImages(operation, id);

      case 'CREATE_NEW_CUSTOMER':
        return await this.handleCreateNewCustomer(variables, id);

      default:
        throw new Error(`Unknown operation type: ${type}`);
    }
  }

  /**
   * Process all pending operations for a store
   * Returns results array with success/failure status for each operation
   */
  async processPendingOperationsForStore(storeName, operations) {
    return processSyncOperations(operations, async (operation) => {
      try {
        console.log(
          `[SyncService] Processing ${operation.type} (${operation.id}) from store: ${storeName}`,
        );

        const result = await this.executeOperation(operation);

        if (result?.pendingPricingReview) {
          return { operationId: operation.id, pendingPricingReview: true };
        }

        return {
          operationId: operation.id,
          success: true,
          result,
        };
      } catch (error) {
        console.error(`[SyncService] Operation ${operation.id} failed:`, error);
        const userMessage =
          getTaskUnavailableMessage(error, operation) || error.message;

        return {
          operationId: operation.id,
          operationType: operation.type,
          success: false,
          error: userMessage,
          retriable: isRetriableSyncError(error, operation),
        };
      }
    });
  }
}

// Singleton instance
let syncServiceInstance = null;

/**
 * Initialize the sync service with Apollo Client
 * Must be called during app initialization
 */
export const initializeSyncService = (apolloClient) => {
  if (syncServiceInstance) {
    console.warn('[SyncService] Already initialized, skipping...');
    return syncServiceInstance;
  }

  syncServiceInstance = new OfflineSyncService(apolloClient);
  return syncServiceInstance;
};

/**
 * Get the sync service instance
 * Throws error if not initialized
 */
export const getSyncService = () => {
  if (!syncServiceInstance) {
    throw new Error(
      'SyncService not initialized. Call initializeSyncService(apolloClient) first.',
    );
  }
  return syncServiceInstance;
};

/**
 * Check if sync service is initialized
 */
export const isSyncServiceInitialized = () => {
  return syncServiceInstance !== null;
};
